import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import {
  getSchemas,
  updateItem,
  updateItemName,
  deactivateTemplates,
  changesMade,
  clearChanges,
  updateInvalidVariables
} from '../../actions/actions'

import css from './styles.scss'

import TemplateSelector from '../TemplateSelector'
import TemplateInstructions from '../TemplateInstructions'
import VarList from '../VarList'
import EditVariable from '../EditVariable'
import NewDataSource from '../NewDataSource'
import EditDataSource from '../EditDataSource'

import Amount from './Amount'
import Text from './Text'
import Choose from './Choose'
import Listing from './Listing'
import CheckBox from './CheckBox'
import ItemPreview from '../ItemPreview'
import PreviewItem from '../PreviewItem'
import { isEqual } from 'lodash'
import Blank from './Blank'

const Loading = () => <div className={css.loading}>Loading Item...</div>

export class EditorGUI extends Component {
  constructor ({ item: { name } }) {
    super()
    this.state = {
      report: false,
      saving: false,
      changes: false,
      name: name || '',
      missingNameOnSave: false,
      modal: null
    }
    this.VALIDATORS = {
      randomNumber: this.validateRandomNumber,
      choiceFromDataSource: this.validateChoiceFromDataSource,
      choiceFromDataSourceColumn: this.validateChoiceFromDataSourceColumn,
      choiceFromDataSourceColumnName: this.validateChoiceFromDataSourceColumnName,
      getter: this.validateGetter,
      replace: this.validateReplace,
      round: this.validateRound,
      formula: this.validateFormula,
      operator: this.validateOperator,
      javascript: this.validateJavascript,
      math: this.validateMath
    }
    this.controller = AbortController ? new AbortController() : {}
  }

  getCurrentItemData = evt => {
    const changedBefore = this.state.changes
    return new Promise((resolve, reject) => {
      this.props.dispatch(updateItemName(this.state.name))
      this.setState({
        report: true
      }, () => {
        this.setState({
          report: false
        }, () => {
          resolve(this.props)
          if (!changedBefore) {
            this.props.dispatch(clearChanges())
          }
        })
      })
    })
  }

  createReferenceLookup = variables => {
    const referenceLookup = {}

    for (const key in variables) {
      const variable = variables[key]

      referenceLookup[key] = new Set()

      const { macro, args, modifiers } = variable

      if (modifiers) {
        for (const modifier of modifiers) {
          if (modifier.name === 'replace') {
            const [oldValue, newValue, oldValueIsReference, newValueIsReference] = modifier.args

            if (oldValueIsReference) {
              referenceLookup[key].add(oldValue)
            }

            if (newValueIsReference) {
              referenceLookup[key].add(newValue)
            }
          }
        }
      }

      if (macro === 'choiceFromDataSource') {
        const [, filters] = args

        if (filters) {
          filters.filter(arg => arg.isReference).forEach(arg => referenceLookup[key].add(arg.value))
        }

        continue
      }

      if (macro === 'randomNumber') {
        const [value, value2, valueIsReference, value2IsReference, toNearest, avoid] = args
        
        if (valueIsReference) {
          referenceLookup[key].add(value)
        }

        if (value2IsReference) {
          referenceLookup[key].add(value2)
        }

        if (Array.isArray(avoid) && avoid.length) {
          avoid.filter(arg => typeof arg === 'string').forEach(arg => referenceLookup[key].add(arg))
        }

        continue
      }

      if (macro === 'formula') {
        const [steps] = args

        if (steps) {
          steps.filter(arg => arg.type === 'reference').forEach(arg => referenceLookup[key].add(arg.value))
          steps.filter(arg => arg.type === 'operator' && arg.value === 'reference').forEach(arg => referenceLookup[key].add(arg.selected[0]))
        }

        continue
      }

      if (macro === 'getter') {
        const [source, select, isReference] = args

        referenceLookup[key].add(source)

        if (isReference) {
          referenceLookup[key].add(select)
        }

        continue
      }

      if (macro === 'round' || macro === 'replace' || macro === 'operator') {
        const [value] = args

        referenceLookup[key].add(value)
      }
    }

    return referenceLookup
  }

  namesOfInvalidVariables = (variables, variableOrder) => {
    if (!variableOrder) {
      return []
    }

    const referenceLookup = this.createReferenceLookup(variables)

    const namesOfInvalidVariables = []

    for (const [index, key] of variableOrder.entries()) {
      const references = referenceLookup[key]

      const referencesArray = Array.from(references)

      for (const reference of referencesArray) {
        const indexOfReference = variableOrder.indexOf(reference)

        if (indexOfReference !== -1 && indexOfReference > index) {
          const { name } = variables[key]
          namesOfInvalidVariables.push(name)
          break
        }
      }
    }

    return namesOfInvalidVariables
  }

  handleSave = evt => {
    if (this.state.name === '') {
      this.setState({
        missingNameOnSave: true
      })
      return
    }
    let method = 'POST'
    let route = '/api/v1/items'
    let showFailedToSaveAlert = true
    this.setState({ saving: true })
    this.getCurrentItemData(evt).then(props => {
      // at this point redux has stored all the data and it's ready to be shipped off
      const { item: { id, data, variables, variableOrder, dataSources }, templates: { selected: templateIds } } = props

      const invalidNames = this.namesOfInvalidVariables(variables, variableOrder)

      if (invalidNames.length) {
        const multiple = invalidNames.length > 1

        let formattedNames = invalidNames[0]

        if (multiple) {
          const last = invalidNames.pop()
          formattedNames = invalidNames.join(', ') + ' and ' + last
        }

        const message = `${formattedNames} reference${ multiple ? '' : 's' } other variables that are below ${ multiple ? 'them' : 'it' } in order. Although this will most likely still work, it is encouraged to place variables below the ones that they reference. Would you like to save anyway?`
        const confirmed = confirm(message)

        if (!confirmed) {
          return showFailedToSaveAlert = false
        }
      }

      // const templateIds = templates.map(template => template.id)
      const { name } = this.state
      if (id) {
        method = 'PUT'
        route += '/'
        route += id
      }

      return fetch(route, {
        method,
        signal: this.controller.signal,
        headers: {
          Authorization: `Bearer ${this.props.auth.bearerToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name,
          id,
          data,
          variables,
          variableOrder,
          templateIds,
          dataSources
        })
      })
    }).then(res => {
      if (res.status === 201 || res.status === 200) {
        return res.json()
      }

      return res.text()
    }).then(data => {
      if (typeof data === 'string') {
        alert(data)
        this.setState({
          saving: false,
          missingNameOnSave: false
        })
        return false
      }

      if (method === 'POST') {
        this.props.history.push(`/item/${data.id}`)
      } else {
        this.setState({
          saving: false,
          missingNameOnSave: false
        })
      }
      this.props.dispatch(clearChanges())
      if (window.opener) {
        window.opener.postMessage({ action: 'save' }, '*')
      }
    }).catch(() => {
      this.setState({
        saving: false,
        missingNameOnSave: false
      })
      if (showFailedToSaveAlert) {
        alert('Save unsuccessful. Please check your connection and try again.')
      }
    })
  }

  saveAmount = (key, amount) => {
    this.props.dispatch(updateItem(key, amount))
  }

  saveSelect = (key, choice) => {
    this.props.dispatch(updateItem(key, choice))
  }

  saveText = (key, text) => {
    this.props.dispatch(updateItem(key, text))
  }

  saveCheckBox = (key, checked) => {
    this.props.dispatch(updateItem(key, checked))
  }

  saveListing = (key, listings) => {
    this.props.dispatch(updateItem(key, listings))
  }

  handleUpdateName = evt => {
    this.setState({
      name: evt.target.value
    }, () => this.props.dispatch(changesMade()))
  }

  handleModal = (evt, edit = false, dataSource = false) => {
    if (edit) {
      this.setState({
        modal: 'edit'
      })
    } else if (dataSource) {
      this.setState({
        modal: 'editSource'
      })
    } else if (evt.target.getAttribute('data-modal')) {
      const modal = evt.target.getAttribute('data-modal')
      this.setState({
        modal
      })
    }
  }

  handleClearModal = () => {
    this.setState({
      modal: null
    })
  }

  validateRandomNumber = args => {
    const { item: { variableOrder: order } } = this.props
    const min = args[0] || args[0] === 0 ? args[0] : ''
    const max = args[1] || args[1] === 0 ? args[1] : ''
    const minIsReference = args[2] || false
    const maxIsReference = args[3] || false
    const avoid = args[5] || []
    if (minIsReference && order.indexOf(min) === -1) {
      return false
    }
    if (maxIsReference && order.indexOf(max) === -1) {
      return false
    }
    if (min === '' || max === '') {
      return false
    }
    for (const num of avoid) { // eslint-disable-line no-unused-vars
      if (isNaN(num) && order.indexOf(num) === -1) {
        return false
      }
    }
    return true
  }

  validateChoiceFromDataSource = args => {
    const { item: { variableOrder: order, dataSources } } = this.props
    const selectedDataSource = args[0] || ''
    const filters = args[1] || []
    if (!selectedDataSource || !dataSources.hasOwnProperty(selectedDataSource)) { // eslint-disable-line no-prototype-builtins
      return false
    }
    for (const filter of filters) { // eslint-disable-line no-unused-vars
      if (!filter.column || !filter.value) {
        return false
      }
      if (filter.isReference && order.indexOf(filter.value) === -1) {
        return false
      }
    }
    return true
  }

  validateChoiceFromDataSourceColumn = args => {
    const { item: { variableOrder: order, dataSources } } = this.props
    const selectedDataSource = args[0] || ''
    const selectedColumn = args[1] || ''
    const columnNameIsReference = args[2] || false
    if (!selectedDataSource || !dataSources.hasOwnProperty(selectedDataSource)) { // eslint-disable-line no-prototype-builtins
      return false
    }
    if (columnNameIsReference && order.indexOf(selectedColumn) === -1) {
      return false
    }
    return true
  }

  validateChoiceFromDataSourceColumnName = args => {
    const { item: { dataSources } } = this.props
    const selectedDataSource = args[0] || ''
    return selectedDataSource && dataSources.hasOwnProperty(selectedDataSource) // eslint-disable-line no-prototype-builtins
  }

  validateGetter = args => {
    const { item: { variableOrder: order } } = this.props
    const source = args[0] || ''
    const keyOrIdx = args[1] || ''
    const isVariable = args[2] || false
    if (!source || order.indexOf(source) === -1) {
      return false
    }
    if (!keyOrIdx) {
      return false
    }
    if (isVariable && order.indexOf(keyOrIdx) === -1) {
      return false
    }
    return true
  }

  validateReplace = args => {
    const { item: { variableOrder: order } } = this.props
    const reference = args[0] || ''
    const oldVal = args[1] || ''
    if (!reference || order.indexOf(reference) === -1) {
      return false
    }
    if (!oldVal) {
      return false
    }
    return true
  }

  validateRound = args => {
    const { item: { variableOrder: order } } = this.props
    const reference = args[0] || ''
    const roundAmt = args[1] || ''
    if (!reference || order.indexOf(reference) === -1) {
      return false
    }
    if (!roundAmt) {
      return false
    }
    return true
  }

  validateFormula = args => {
    const { item: { variableOrder: order } } = this.props
    const steps = args[0] || []
    for (const [index, step] of steps.entries()) { // eslint-disable-line no-unused-vars
      const isLastStep = index === steps.length - 1
      if (step.type === 'value' && !step.value) {
        return false
      }
      if (step.type === 'reference' && order.indexOf(step.value) === -1) {
        return false
      }
      if (!isLastStep && step.type === 'operator') {
        if (!step.value) {
          return false
        }
        if (step.value === 'reference' && step.selected && !step.selected[0]) {
          return false
        }
      }
    }
    return true
  }

  validateOperator = args => {
    const { item: { variables } } = this.props
    const [variableId, index] = args
    const variable = variables[variableId]
    if (!variable) {
      return false
    }
    const [steps] = variable.args
    const operators = steps.filter(step => step.type === 'operator')
    const step = operators[index]
    if (!step || !step.value) {
      return false
    }
    return true
  }

  validateJavascript = args => {
    if (args[0]) {
      return args[0].length
    }
    return false
  }

  validateMath = args => {
    return args[0] && args[0].length
  }

  isValid (variable) {
    const { args, macro } = variable
    if (!this.VALIDATORS[macro]) {
      return true
    }
    return this.VALIDATORS[macro](args)
  }

  componentDidMount () {
    const { item, templates: { selected }, app: { previewUrl } } = this.props
    if (item && item.templateIds && item.templateIds.length > 0) {
      this.props.dispatch(getSchemas(item.templateIds))
    }
    if (!selected.length && previewUrl) {
      this.setState({
        modal: 'template'
      })
    }
  }

  componentWillUnmount () {
    this.props.dispatch(deactivateTemplates())
    this.controller.abort()
  }

  componentWillReceiveProps (nextProps) {
    const { item: { changes, variables, variableOrder } } = nextProps
    const { item: { invalidVariables } } = this.props
    const invalid = []
    const invalidVars = invalidVariables || []
    if (variableOrder) {
      for (const varKey of variableOrder) { // eslint-disable-line no-unused-vars
        if (!this.isValid(variables[varKey])) {
          invalid.push(varKey)
        }
      }
      if (!isEqual(invalid.sort(), invalidVars.sort())) {
        this.props.dispatch(updateInvalidVariables(invalid))
      }
    }
    this.setState({
      changes
    })
  }

  render () {
    const { data, name: savedName, schema, hideNameField } = this.props.item
    const showTemplateSelector = this.props.templates.showTemplateSelector
    const standAloneMode = this.props.app.standAloneMode
    const { report, name, saving, changes, missingNameOnSave, modal } = this.state
    const item = data || {} // since null is a valid argument
    const { app: { primaryColor, secondaryColor, isSafari, previewUrl } } = this.props
    const fields = []
    let nameField = null
    let nameWarning = null
    let saveButtonText = 'Save'
    const tips = [
      'Define variables in the left pane',
      'Drag variables into text fields in the right pane',
      'Click Preview to view a single instance of your item'
    ]
    if (!hideNameField) {
      tips.unshift('Name Your Item')
    }
    if (showTemplateSelector) {
      tips.unshift('Select templates')
    }
    if (missingNameOnSave && name.length === 0) {
      nameWarning = (
        <p className={css.warn}>
          Item name is required
        </p>
      )
    }
    if (!savedName && this.props.id) {
      return <Loading />
    }
    if (!savedName && this.props.id) {
      return <Loading />
    }
    if (changes) {
      saveButtonText = 'Save'
    }
    if (saving) {
      saveButtonText = 'Saving...'
    }
    if (!hideNameField) {
      nameField = (
        <div className={css.name}>
          {nameWarning}
          <label className={css.label}>Name</label>
          <input type='text' value={name} onChange={this.handleUpdateName} className={css.input} />
        </div>
      )
    }
    if (schema) {
      const keys = Object.keys(schema)

      for (let i = 0, numKeys = keys.length; i < numKeys; i++) {
        const dataKey = keys[i]
        const localSchema = schema[dataKey]
        const itemData = item[dataKey]
        const comProps = {
          schema: localSchema,
          key: dataKey,
          dataKey,
          report,
          isSafari
        }
        let component = null
        switch (localSchema.type) {
          case 'number':
            component = (
              <Amount
                {...comProps}
                quantity={itemData}
                save={this.saveAmount}
              />
            )
            break
          case 'text':
            component = (
              <Text
                {...comProps}
                string={itemData}
                save={this.saveText}
                modal={this.state.modal}
              />
            )
            break
          case 'boolean':
            component = (
              <CheckBox
                {...comProps}
                checked={itemData}
                save={this.saveCheckBox}
              />
            )
            break
          case 'select':
            component = (
              <Choose
                {...comProps}
                save={this.saveSelect}
                choice={itemData}
              />
            )
            break
          case 'array':
            component = (
              <Listing
                {...comProps}
                save={this.saveListing}
                list={itemData}
              />
            )
            break
          case 'alias':
            // this does not yield a component
            break
          case 'segment':
            component = (
              <Blank
                {...comProps}
              />
            )
          default:
            break
        }
        fields.push(component)
      }
    }

    let bottomOptions = null
    if (!modal || modal === 'template') {
      bottomOptions = (
        <div className={css.bottomOptions}>
          <button type='button' onClick={this.handleSave} className={css.save} style={{ backgroundColor: secondaryColor }}>{saveButtonText}</button>
        </div>
      )
    } else if (modal === 'edit' || modal === 'editSource') {
      bottomOptions = <button type='button' onClick={this.handleSave} className={css.save} style={{ backgroundColor: secondaryColor }}>{saveButtonText}</button>
    }

    let previewComponent = null
    if (previewUrl) {
      previewComponent = <PreviewItem modal={this.state.modal} onClearModal={this.handleClearModal} onHandleSave={this.handleSave} saving={saving} />
    }

    return (
      <article className={`${css.EditorGUI} ${standAloneMode ? css.EditorGUIStandAlone : ''}`}>
        <section className={`${css.container} ${standAloneMode ? css.containerStandAlone : ''}`}>
          <div className={css.vars} onClick={this.handleModal}>
            <div className={css.topNav}>
              {previewComponent}
              <ItemPreview getCurrentItemData={this.getCurrentItemData} modal={this.state.modal} onClearModal={this.handleClearModal} />
            </div>
            <button type='button' onClick={this.handleClearModal} className={`${css.btn} ${!this.state.modal ? css.currentTab : null}`} style={{ background: !this.state.modal ? primaryColor : null }}>Item Details</button>
            <NewDataSource modal={this.state.modal} onHandleModal={this.handleModal} onClearModal={this.handleClearModal} />
            <VarList onHandleModal={this.handleModal} modal={this.state.modal} />
          </div>
          <div className={css.list}>
            <div className={css.listContent}>
              <EditVariable modal={this.state.modal} onHandleModal={this.handleModal} onClearModal={this.handleClearModal} />
              <EditDataSource modal={this.state.modal} onClearModal={this.handleClearModal} />
              <TemplateInstructions />
              <ul className={css.fields}>
                <li>
                  <TemplateSelector />
                </li>
                <li>
                  {nameField}
                </li>
                {fields}
              </ul>
            </div>
            {bottomOptions}
          </div>
        </section>
      </article>
    )
  }
}

const mapStateToProps = state => {
  const { app, auth, item, templates, variables } = state
  return {
    app,
    auth,
    item,
    templates,
    variables
  }
}

export default withRouter(connect(mapStateToProps)(EditorGUI))



// WEBPACK FOOTER //
// /app/js/components/EditorGUI/index.js